import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Shield } from 'lucide-react'

export default function Privacy() {
  const navigate = useNavigate()
  return (
    <div className="min-h-[100dvh] bg-[#0a0a12] overflow-x-hidden">
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[128px] -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[128px] translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto py-8 px-4">
        <div className="backdrop-blur-xl bg-slate-900/80 border border-cyan-500/20 rounded-2xl shadow-2xl shadow-cyan-500/10">
          <div className="p-6 md:p-10 space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <button onClick={() => navigate(-1)} className="text-cyan-400 hover:text-cyan-300 transition-colors">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <Shield className="w-6 h-6 text-cyan-400" />
              <h1 className="text-xl md:text-2xl font-bold text-white tracking-wide">PRIVACY POLICY</h1>
            </div>
            <p className="text-slate-400 text-xs font-mono">Last updated: February 10, 2026</p>

            <div className="space-y-6 text-slate-300 text-sm leading-relaxed">
              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">1. Introduction</h2>
                <p>
                  Territory Nexus ("we", "us", "our") operates the Territory Nexus mobile web application
                  (the "Service"). This Privacy Policy explains how we collect, use, and protect your personal
                  information when you use our Service.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">2. Information We Collect</h2>
                <p>We collect the following categories of information:</p>
                <ul className="list-disc list-inside space-y-1 text-slate-400">
                  <li><span className="text-slate-300">Account Information:</span> Username and credentials you provide during registration.</li>
                  <li><span className="text-slate-300">Location Data:</span> GPS coordinates from your device, used to enable location-based gameplay. Collected only while the app is open and actively in use.</li>
                  <li><span className="text-slate-300">Gameplay Data:</span> In-game actions, units, resources, battle history, and other game state tied to your account.</li>
                  <li><span className="text-slate-300">Technical Data:</span> IP address and connection metadata for security and anti-cheat purposes.</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">3. How We Use Your Information</h2>
                <ul className="list-disc list-inside space-y-1 text-slate-400">
                  <li>To provide and operate the location-based game experience.</li>
                  <li>To verify your proximity to in-game zones (GPS data).</li>
                  <li>To authenticate your account and prevent unauthorized access.</li>
                  <li>To detect cheating, abuse, and enforce our Terms of Service.</li>
                  <li>To improve the Service and fix bugs.</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">4. Location Data</h2>
                <p>
                  Location data is a core part of Territory Nexus gameplay. We collect your GPS coordinates
                  when the app is open to verify you are physically near game zones before allowing actions
                  such as claiming zones, deploying units, or initiating battles.
                </p>
                <p>
                  Your last known location is stored on our servers and overwritten with each update
                  while the app is active. We do not sell, share, or use your location data for advertising.
                  You can stop location collection at any time by closing the app or revoking location
                  permissions in your browser/device settings.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">5. Data Storage and Security</h2>
                <p>
                  Your data is stored on secure servers. All data in transit is encrypted via HTTPS/TLS.
                  We use industry-standard security measures to protect your data.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">6. Data Retention</h2>
                <p>
                  We retain your account and gameplay data for as long as your account is active.
                  If you delete your account, your personal data is removed immediately.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">7. Your Rights</h2>
                <p>Depending on your jurisdiction, you may have the right to:</p>
                <ul className="list-disc list-inside space-y-1 text-slate-400">
                  <li>Access the personal data we hold about you.</li>
                  <li>Request correction of inaccurate data.</li>
                  <li>Request deletion of your account and personal data.</li>
                  <li>Export your data in a portable format.</li>
                  <li>Withdraw consent for location tracking (by disabling GPS permissions).</li>
                </ul>
                <p>
                  To exercise any of these rights, contact us at the email below.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">8. Children's Privacy</h2>
                <p>
                  Territory Nexus is not intended for children under 13 years of age. We do not knowingly
                  collect personal information from children under 13. If you are under 13, please do not
                  register or use the Service. If we learn that we have collected data from a child under 13,
                  we will delete that information promptly.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">9. Third-Party Services</h2>
                <p>We use the following third-party services:</p>
                <ul className="list-disc list-inside space-y-1 text-slate-400">
                  <li><span className="text-slate-300">Supabase:</span> Authentication services.</li>
                  <li><span className="text-slate-300">Mapbox:</span> Map rendering.</li>
                </ul>
                <p>Each third party has its own privacy policy governing their use of data.</p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">10. Changes to This Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of material changes
                  by posting the updated policy in the app. Continued use of the Service after changes
                  constitutes acceptance of the updated policy.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-base font-semibold text-white">11. Contact</h2>
                <p>
                  If you have questions about this Privacy Policy or wish to exercise your data rights,
                  please contact us at <span className="text-cyan-400">support@brokenzones.com</span>.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
